import React from "react"
import { Button, Flex } from "antd"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { Title, Text } from "ui/Typography"
import BookingCard from "./BookingCard"
import styles from "./index.module.css"
import { Modal } from "ui/Modal"

const RemoveExperienceWithBookingsModal = ({
  open,
  onCancel,
  resourceName,
  experienceName,
  bookings = [],
  conflictingExperiences = [],
  resourceType = "crew", // "crew" or "equipment"
  titleKey = "crewMember.removeExperience.title",
  bookingsLabelKey = "crewMember.removeExperience.bookingsLabel",
  removeButtonKey = "crewMember.removeExperience.removeButton"
}) => {
  const { t } = useTranslation()
  // Determine if we're showing grouped (multiple experiences) or single experience view
  const isGroupedView = conflictingExperiences.length > 0
  const isEquipment = resourceType === "equipment"

  const getInfoText = () => {
    if (isEquipment) {
      return `Equipment: <strong>${resourceName}</strong> has bookings for the following experiences that cannot be unassigned.<br />All bookings must be reassigned before these experiences can be removed.`
    }

    if (isGroupedView) {
      return `Crew Member: <strong>${resourceName}</strong> has bookings for the following experiences that cannot be unassigned.<br />All bookings must be reassigned before these experiences can be removed.`
    }

    return `Crew Member: <strong>${resourceName}</strong> is currently assigned to bookings for Experience: <strong>${experienceName}</strong>.<br />All bookings for this experience must be reassigned to a different crew member before it can be removed.<br /><br />Once all bookings have been updated, return here to remove the experience.`
  }

  return (
    <Modal open={open} onCancel={onCancel} width={431} centered maskClosable={false}>
      <Flex vertical gap={24} className={styles.container}>
        {/* Header */}
        <div className={styles.header}>
          <Title level={3}>{t(titleKey, { defaultValue: "Remove Experience" })}</Title>
        </div>

        {/* Content */}
        <Flex vertical gap={24}>
          {/* Info Text */}
          <div className={styles.infoSection}>
            <div className={styles.infoText}>
              <span
                dangerouslySetInnerHTML={{
                  __html: getInfoText()
                }}
              />
            </div>
          </div>

          {/* Bookings List - Single Experience View */}
          {!isGroupedView && bookings?.length > 0 && (
            <Flex vertical gap={12}>
              <Text fw={700} className={styles.bookingsLabel}>
                {t(bookingsLabelKey, { defaultValue: "Bookings:" })}
                {bookings.length > 1 && <span className="total-count-color">({bookings.length})</span>}
              </Text>

              <div className={styles.bookingsContainer}>
                <Flex vertical gap={12} className={styles.bookingsList}>
                  {bookings
                    .filter((booking) => booking?.id)
                    .map((booking) => (
                      <BookingCard key={booking.id} booking={booking} />
                    ))}
                </Flex>
              </div>
            </Flex>
          )}

          {/* Bookings List - Grouped by Experience View */}
          {isGroupedView && (
            <Flex vertical gap={16}>
              {conflictingExperiences
                .filter((experience) => experience?.experienceName && experience.bookings?.length > 0)
                .map((experience) => {
                  const { experienceName, bookings } = experience || {}
                  return (
                    <Flex vertical gap={12} key={experienceName}>
                      <Text size="lg" fw={600}>
                        {experienceName}
                      </Text>
                      <Text fw={700} className={styles.bookingsLabel}>
                        {t(bookingsLabelKey, { defaultValue: "Bookings:" })}
                        {bookings.length > 1 && <span className="total-count-color">({bookings.length})</span>}
                      </Text>

                      <div className={styles.bookingsContainer}>
                        <Flex vertical gap={12} className={styles.bookingsList}>
                          {bookings
                            .filter((booking) => booking?.id)
                            .map((booking) => (
                              <BookingCard key={booking.id} booking={booking} />
                            ))}
                        </Flex>
                      </div>
                    </Flex>
                  )
                })}
            </Flex>
          )}
        </Flex>

        {/* Footer */}
        <Flex gap={12}>
          <Button onClick={onCancel} variant="outlined" color="primary">
            {t("common.cancel", { defaultValue: "Cancel" })}
          </Button>
          <Button disabled variant="outlined" color="primary">
            {t(removeButtonKey, { defaultValue: "Remove experience" })}
          </Button>
        </Flex>
      </Flex>
    </Modal>
  )
}

RemoveExperienceWithBookingsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  resourceName: PropTypes.string.isRequired,
  experienceName: PropTypes.string,
  bookings: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      experienceName: PropTypes.string.isRequired,
      imageUrl: PropTypes.string,
      location: PropTypes.string,
      date: PropTypes.string,
      time: PropTypes.string,
      booking_uuid: PropTypes.string
    })
  ),
  conflictingExperiences: PropTypes.arrayOf(
    PropTypes.shape({
      experienceName: PropTypes.string.isRequired,
      bookings: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
          experienceName: PropTypes.string.isRequired,
          imageUrl: PropTypes.string,
          location: PropTypes.string,
          date: PropTypes.string,
          time: PropTypes.string,
          booking_uuid: PropTypes.string
        })
      ).isRequired
    })
  ),
  resourceType: PropTypes.oneOf(["crew", "equipment"]),
  titleKey: PropTypes.string,
  bookingsLabelKey: PropTypes.string,
  removeButtonKey: PropTypes.string
}

export default RemoveExperienceWithBookingsModal
